/**
 * An opponent to practise against, at a strength you choose.
 *
 * Weakness here comes from **thinking less**, not from throwing games. A bot
 * that plays well and then deliberately hangs a piece teaches the wrong lesson
 * twice: the learner cannot tell a gift from a real chance, and the win they
 * eventually get is not one they earned. A shallow search misses the same
 * things a human misses at the same level, and gets punished the same way.
 *
 * The one concession is a little variety among moves the search rates as
 * equivalent. Without it every game from the same opening is the same game, and
 * practice against a fixed reply is memorisation rather than understanding.
 * That choice is the only randomness, it is injectable, and it never reaches
 * outside the set of moves the engine already considers fine.
 *
 * Pure apart from the injected clock and RNG.
 */
import type { State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { seriousLossUnit } from './review.ts';
import { isDecisive, searchPosition } from './search.ts';

/**
 * How hard the bot is trying.
 *
 * Named for how they play rather than as numbers, because "level 3" tells a
 * learner nothing about what they are about to meet.
 */
export const BOT_LEVELS = ['learner', 'steady', 'sharp'] as const;
export type BotLevel = (typeof BOT_LEVELS)[number];

interface LevelConfig {
  /** Wall-clock milliseconds for the search. */
  readonly budgetMs: number;
  /**
   * Deepest iteration to attempt. This is what actually sets the strength: a
   * four-ply bot sees the win in front of it and the reply, and no further.
   */
  readonly maxDepth: number;
  /**
   * How far below the best score a move may be and still get picked, as a
   * multiple of one nearly-complete line. Zero means it always plays the best
   * move it found.
   */
  readonly slack: number;
}

/**
 * Depths chosen so each level fails in a recognisable way.
 *
 * `learner` sees an immediate win and an immediate threat but not a fork being
 * built, which is exactly the wall a beginner is standing at. `steady` sees the
 * fork coming. `sharp` searches properly and takes the best move it found, so
 * the endgame is unforgiving.
 */
const LEVELS: Record<BotLevel, LevelConfig> = {
  learner: { budgetMs: 30, maxDepth: 4, slack: 1 },
  steady: { budgetMs: 80, maxDepth: 8, slack: 0.5 },
  sharp: { budgetMs: 250, maxDepth: 42, slack: 0 },
};

export interface BotChoice {
  /** The move to play. */
  readonly param: number;
  /** The best move the search found, which may be the same one. */
  readonly bestParam: number;
  /** Score of the chosen move, from the bot's point of view. */
  readonly score: number;
  readonly bestScore: number;
  /** Deepest completed iteration. Useful for saying how hard it looked. */
  readonly depth: number;
  /**
   * True when the bot played something other than its own top choice.
   *
   * Reported rather than hidden: a practice screen that claims the bot is
   * playing its best while it samples among equals would be lying about the
   * one thing the learner is trying to measure themselves against.
   */
  readonly variedFromBest: boolean;
}

export interface BotOptions {
  readonly level?: BotLevel;
  /** Injectable, so a test can pin the choice among equal moves. */
  readonly random?: () => number;
  readonly now?: () => number;
  /** Overrides the level's budget. Tests use it to keep the suite quick. */
  readonly budgetMs?: number;
  /**
   * Overrides the level's depth cap.
   *
   * The bot is reproducible only when the search is. A wall-clock budget makes
   * depth a function of how busy the machine is, so pinning the depth is the
   * only way to ask for the same move twice and mean it.
   */
  readonly maxDepth?: number;
}

/**
 * Choose a move for the seat to play in `state`.
 *
 * Returns null only when there is nothing to choose: a finished game, or a
 * position with no legal move. Callers are expected to check the status first;
 * this is the belt to that pair of braces.
 */
export function chooseBotMove<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  options: BotOptions = {},
): BotChoice | null {
  if (state.status.kind !== 'in_play') return null;

  const level = LEVELS[options.level ?? 'steady'];
  const search = searchPosition(rules, state, {
    budgetMs: options.budgetMs ?? level.budgetMs,
    maxDepth: options.maxDepth ?? level.maxDepth,
    ...(options.now === undefined ? {} : { now: options.now }),
  });

  const best = search.moves[0];
  if (best === undefined) return null;

  const shape = {
    bestParam: best.param,
    bestScore: best.score,
    depth: search.depth,
  };

  /*
   * Never wander when the position is decided.
   *
   * A proven win must be converted and a proven loss must be resisted for as
   * long as possible. Sampling here would show a learner a bot that "had it and
   * let it go", which reads as the board being random rather than as their own
   * defence working.
   */
  if (level.slack === 0 || isDecisive(best.score)) {
    return { param: best.param, score: best.score, variedFromBest: false, ...shape };
  }

  const margin = seriousLossUnit(rules) * level.slack;
  // Equal-enough moves, in the search's own order, so the pool is deterministic
  // before the one random draw that picks from it.
  const pool = search.moves.filter(
    (move) => !isDecisive(move.score) && best.score - move.score <= margin,
  );
  if (pool.length <= 1) {
    return { param: best.param, score: best.score, variedFromBest: false, ...shape };
  }

  const random = options.random ?? Math.random;
  const index = Math.min(pool.length - 1, Math.floor(random() * pool.length));
  const chosen = pool[index] ?? best;

  return {
    param: chosen.param,
    score: chosen.score,
    variedFromBest: chosen.param !== best.param,
    ...shape,
  };
}

/** How the level should be described on screen. */
export function describeLevel(level: BotLevel): string {
  switch (level) {
    case 'learner':
      return 'Sees the move in front of it. Misses what you are building.';
    case 'steady':
      return 'Spots traps a move or two ahead.';
    case 'sharp':
      return 'Searches hard and takes its chances.';
  }
}
